import { Component, computed, inject, Signal, ChangeDetectionStrategy } from '@angular/core';
import { Sort } from '../../shared/components/table/table/table.component';
import { Observable } from 'rxjs';
import { maxDeelVanJaar, maxOver, maxOverMapped, noAgg0, sumOver } from '../../services/aggregation';
import { Path } from '../../services/data-tree';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	defaultDoorstroomAvailableGroups,
	DoorstroomMeasure,
	ExportDataOptions,
	xAggExcept,
} from '../../services/data.service';
import { att, att0, count_records, percOfTotal } from '../../services/measures';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { defaultDoorstroomActueelFilters, defaultDoorstroomHistorieFilters } from '../../services/filter-config';
import { range } from 'lodash-es';
import { DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { AttrPath, Table } from '@cumlaude/metadata';
import { Axis, createYAxis } from '../../services/axis';
import { DashboardVariant, Eenheid } from '../../services/weergave-opties';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { PsName } from '../../services/page-state.service';
import { BarInfo } from '../../services/stacked-bars';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { formatPercent } from '@angular/common';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

interface InstroomI extends Attributes {
	ds_m_nr_weging: number;
	'ds_fk_lb_van.lb_is_pl_voorlopig': string;
	xa: { [nr: number]: { ds_m_nr_weging: number } };
	ds_m_nr_leerlingen: number;
}

interface InstroomA extends Attributes {
	max: number;
	weging: number;
	ds_is_lb_voorlopig: number;
	maxPctJaar: number;
	ds_m_nr_leerlingen: number;
}

enum MeasureColumn {
	LEERLINGEN = 'Leerlingen',
	PERCENTAGE = 'Percentage',
}

const historieSchooljaarGroup: AttrPath = ['ds_nm_schooljaar_van'];

@Component({
	selector: 'app-instroom',
	templateUrl: './instroom.component.html',
	styleUrls: ['./instroom.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		VbarchartTableComponent,
		WeergaveOptieComponent,
		FilterBarComponent,
		ToggleButtonsComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstroomComponent extends BarchartTableConfig<InstroomI, InstroomA> {
	private readonly dataService = inject(DataService);

	fixedSubGroups: AttrPath[] = [['ds_fk_lb_van', 'lb_is_pl_voorlopig']];

	historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));

	historieSubgroups = computed(() => {
		const huidigeGroeperingen = this.groeperingService.huidigeGroeperingen();
		return [...huidigeGroeperingen.slice(-1), historieSchooljaarGroup, ...this.fixedSubGroups];
	});

	override availableGroups: AttrPath[] = [
		...defaultDoorstroomAvailableGroups,
		['ds_fk_lb_vorig_sj', 'lb_nm_vestiging'],
		['ds_fk_lb_van', 'lb_nm_instroomtype_in_schooljaar'],
		['ds_fun_nm_instroommoment'], // Instroommoment (uitgedrukt als tekst, bijv. 'T/m 1 oktober', 'Na 1 oktober', etc.)
	];

	actueelFilters: FilterName[] = [
		...defaultDoorstroomActueelFilters,
		'ds_fk_lb_van.lb_nm_instroomtype_in_schooljaar',
		'ds_fun_nr_maanden_instroom_na_1okt', // Instroommoment (filter op numerieke waarde vanaf gekozen maand)
	];

	historieFilters: FilterName[] = [
		...defaultDoorstroomHistorieFilters,
		'ds_fk_lb_van.lb_nm_instroomtype_in_schooljaar',
		'ds_fun_nr_maanden_instroom_na_1okt', // Instroommoment (filter op numerieke waarde vanaf gekozen maand)
	];

	permanentFilterExpressions = [
		new BasicFilterExpression(['ds_fun_is_plaatsing_peildatum_actueel'], 1),
		new BasicFilterExpression(['ds_is_plaatsing_opeenvolgend'], 1),
		new BasicFilterExpression(['ds_nm_instroom_in_schooljaar'], null, '<>'),
	];

	variant = this.qp.signal('variant');

	eenheid = this.qp.signal('eenheid');

	factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		const xa = this.variant() === DashboardVariant.ACTUEEL ? [] : [xAggExcept(options.g!, historieSchooljaarGroup)];
		return this.dataService.getDoorstroomData({ ...options, xa, m: [DoorstroomMeasure.LEERLINGEN], r: range(0, this.getRowLevel() + 1) });
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getDoorstroomExportData(options);
	}

	protected override singleAggregators = {
		max: maxOver('ds_m_nr_weging'),
		weging: sumOver<'ds_m_nr_weging', InstroomI, number>('ds_m_nr_weging'),
		ds_is_lb_voorlopig: maxOverMapped<InstroomI>((v) => Number(v['ds_fk_lb_van.lb_is_pl_voorlopig'])),
		maxPctJaar: maxDeelVanJaar('ds_m_nr_weging'),
		ds_m_nr_leerlingen: noAgg0<'ds_m_nr_leerlingen', InstroomI>('ds_m_nr_leerlingen'),
	};

	override createMeasureColumns(context: DashboardContext<InstroomI, InstroomA, InstroomComponent>): ColumnDef<DataRow<InstroomA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		return [
			this.createLeerlingColumn(MeasureColumn.LEERLINGEN, att('weging'), { context, format: '1.0-2' }, count_records),
			this.createLeerlingColumn(MeasureColumn.PERCENTAGE, percOfTotal('weging', 'weging'), { context, dataType: 'percentage' }, count_records),
		];
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	override getBarchartQty(path: Path<InstroomA, number[]>) {
		if ((this.variant() === DashboardVariant.HISTORIE && this.eenheid()) == Eenheid.PERCENTAGE) return att0('maxPctJaar')(path);
		else return att0('weging')(path);
	}

	override createYAxis(context: DashboardContext<InstroomI, InstroomA, InstroomComponent>): Axis {
		if (!context.dataRoot) return super.createYAxis(context);
		if (this.eenheid() === Eenheid.AANTAL) {
			return createYAxis([0, context.dataRoot.a.max]);
		} else {
			return createYAxis([0, context.dataRoot.a.maxPctJaar], 5, (val) => formatPercent(val, 'nl_NL'));
		}
	}

	override createXAxis(context: DashboardContext<InstroomI, InstroomA, InstroomComponent>): Axis {
		return { min: 0, max: context.dataRoot!.a.max, ticks: [] };
	}

	override getDefaultSort(): Sort {
		return { active: MeasureColumn.LEERLINGEN, direction: 'desc' };
	}

	getRowLevel() {
		return this.variant() === DashboardVariant.ACTUEEL ? this.groeperingService.huidigeAantalGroeperingen() : this.historieGroups().length;
	}

	override enrichTableModel(_context: DashboardContext<InstroomI, InstroomA, InstroomComponent>, tableModel: TableModel<DataRow<InstroomA>>) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	override makeBar(attrs: InstroomI, path: Path<InstroomA, number[]>, context: DashboardContext<InstroomI, InstroomA, InstroomComponent>): BarInfo {
		const { 'ds_fk_lb_van.lb_is_pl_voorlopig': ds_is_lb_voorlopig, ds_m_nr_weging } = attrs;

		return {
			...super.makeBar(attrs, path, context),
			description: 'Instroom',
			className: ds_is_lb_voorlopig == '1' ? 'dashboard-prognose' : 'dashboard-default',
			tooltip: [
				{ label: 'Prognose', value: ds_is_lb_voorlopig == '1' ? 'Ja' : 'Nee' },
				{ label: 'Leerlingen', value: `${ds_m_nr_weging}` },
			],
		};
	}

	override onContextCreated(context: DashboardContext<InstroomI, InstroomA, InstroomComponent>) {
		this.pageStateService.dispatch(PsName.prognose, String(Boolean(context.dataRoot?.a.ds_is_lb_voorlopig)));
	}

	override getSelectionConfig(context: DataRequestContext<InstroomA>): SelectionConfig<InstroomA> | undefined {
		return { ...super.getSelectionConfig(context)!, getSize: att('ds_m_nr_leerlingen') };
	}

	partitionMeasure: Signal<PartitionMeasure<InstroomA>> = computed(() => {
		if (this.eenheid() === Eenheid.AANTAL)
			return {
				type: 'number',
				getValue: att('weging'),
				format: '1.0-2',
			};
		else
			return {
				type: 'percentage',
				getValue: att('maxPctJaar'),
			};
	});

	protected readonly Eenheid = Eenheid;
	protected readonly DashboardVariant = DashboardVariant;
}
